import * as Sentry from "@sentry/react";
import type {AxiosError} from "axios";

import {resolveAxiosDeviceFailureOverride} from "./axios-device-failure";
import {
  DEVICE_FAILURE_ERROR_CODES,
  ERROR_CODE_FAILURE_DOMAIN,
  type DeviceFailureErrorCode,
} from "./failure-domains";
import {sessionCaptureOnce} from "./capture-session";
import {
  type GlitchtipDiagnostics,
  collectRealtimeClientDiagnostics,
  resolveAxiosCodeTag,
  resolveErrorKind,
  resolveNavigatorOnLineTag,
  resolveNetworkReason,
} from "./diagnostics";
import {resolveErrorCode, SYNTHETIC_ERROR_CODES} from "./error-codes";
import {PLATFORM_FEATURE} from "./feature-registry";
import {getEventTitle, getTelemetryTitle} from "./error-titles";
import type {FailureDomain} from "./failure-domains";
import {extractHttpContextFromAxios, normalizeHttpPathTag, type HttpContext} from "./http-context";
import {resolveFeature, resolveFeatureFromWindowLocation} from "./resolve-feature";
import {resolveAxiosCaptureLevel, shouldCaptureAxiosError} from "./should-capture-axios";
import {shouldCaptureToGlitchtip} from "./should-capture";

const GLITCHTIP_DSN = import.meta.env.VITE_GLITCHTIP_DSN;

const RECENT_CAPTURE_MS = 3_000;
const recentCaptureKeys = new Map<string, number>();

function shouldSkipDuplicateCapture(key: string): boolean {
  const now = Date.now();
  const last = recentCaptureKeys.get(key);
  if (last !== undefined && now - last < RECENT_CAPTURE_MS) {
    return true;
  }
  recentCaptureKeys.set(key, now);
  if (recentCaptureKeys.size > 200) {
    for (const [storedKey, ts] of recentCaptureKeys) {
      if (now - ts > RECENT_CAPTURE_MS) recentCaptureKeys.delete(storedKey);
    }
  }
  return false;
}

export type ErrorSource = "axios" | "boundary" | "domain" | "telemetry";

export function isGlitchTipEnabled(): boolean {
  return Boolean(GLITCHTIP_DSN);
}

/** Push pending events on mobile before the WebView backgrounds (camera flows). */
export function flushGlitchtipEvents(timeout = 2_000): PromiseLike<boolean> {
  if (!GLITCHTIP_DSN) {
    return Promise.resolve(false);
  }
  return Sentry.flush(timeout);
}

function getRouteTag(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const route = window.location.hash.replace(/^#/, "") || window.location.pathname;
  return route || "/";
}

function resolveFailureDomain(
  errorCode: string,
  explicit?: FailureDomain,
): FailureDomain | undefined {
  if (explicit) return explicit;
  if (
    errorCode === SYNTHETIC_ERROR_CODES.NETWORK_ABORTED ||
    errorCode === SYNTHETIC_ERROR_CODES.NETWORK_OFFLINE
  ) {
    return "network";
  }
  if (errorCode in ERROR_CODE_FAILURE_DOMAIN) {
    return ERROR_CODE_FAILURE_DOMAIN[errorCode as DeviceFailureErrorCode];
  }
  return undefined;
}

type CaptureScopeInput = {
  feature: string;
  errorCode?: string;
  errorSource: ErrorSource;
  eventType?: string;
  sheetId?: string;
  failureDomain?: FailureDomain;
  operation?: string;
  http?: HttpContext;
  extra?: Record<string, string | number | undefined>;
  diagnostics?: GlitchtipDiagnostics;
  error?: unknown;
  axiosError?: AxiosError;
};

function shouldAttachNetworkReason(input: CaptureScopeInput): boolean {
  if (input.failureDomain === "network" || input.failureDomain === "realtime") return true;
  if (input.http?.httpStatus === "0") return true;
  if (
    input.errorCode === SYNTHETIC_ERROR_CODES.NETWORK_ABORTED ||
    input.errorCode === SYNTHETIC_ERROR_CODES.NETWORK_OFFLINE
  ) {
    return true;
  }
  return Boolean(input.axiosError && !input.axiosError.response);
}

function mergeDiagnosticsExtra(input: CaptureScopeInput): Record<string, string | number | undefined> {
  const merged: Record<string, string | number | undefined> = {...input.extra};
  const diagnostics = input.diagnostics ?? {};

  const errorKind = diagnostics.error_kind ?? resolveErrorKind(input.error ?? input.axiosError);
  if (errorKind) merged.error_kind = errorKind;

  if (shouldAttachNetworkReason(input)) {
    const networkReason =
      diagnostics.network_reason ?? resolveNetworkReason(input.error, input.axiosError);
    merged.network_reason = networkReason;
  }

  if (diagnostics.realtime_failure_phase) {
    merged.realtime_failure_phase = diagnostics.realtime_failure_phase;
  }
  if (diagnostics.realtime_connect_trigger) {
    merged.realtime_connect_trigger = diagnostics.realtime_connect_trigger;
  }
  if (diagnostics.route_feature) merged.route_feature = diagnostics.route_feature;
  const axiosCode = diagnostics.axios_code ?? resolveAxiosCodeTag(input.axiosError);
  if (axiosCode) merged.axios_code = axiosCode;
  if (diagnostics.config_fetch_attempts !== undefined) {
    merged.config_fetch_attempts = diagnostics.config_fetch_attempts;
  }
  if (diagnostics.reconnect_attempt !== undefined) {
    merged.reconnect_attempt = diagnostics.reconnect_attempt;
  }
  if (diagnostics.document_hidden !== undefined) merged.document_hidden = diagnostics.document_hidden;
  if (diagnostics.visibility_state) merged.visibility_state = diagnostics.visibility_state;
  if (diagnostics.connection_effective_type) {
    merged.connection_effective_type = diagnostics.connection_effective_type;
  }
  if (diagnostics.validation_field_paths) {
    merged.validation_field_paths = diagnostics.validation_field_paths;
  }
  if (diagnostics.field_count !== undefined) merged.field_count = diagnostics.field_count;
  if (diagnostics.had_draft !== undefined) merged.had_draft = diagnostics.had_draft;
  if (diagnostics.had_files !== undefined) merged.had_files = diagnostics.had_files;
  if (diagnostics.geo_error_code !== undefined) merged.geo_error_code = diagnostics.geo_error_code;
  if (diagnostics.sw_active !== undefined) merged.sw_active = diagnostics.sw_active;
  if (diagnostics.config_fetch_duration_ms !== undefined) {
    merged.config_fetch_duration_ms = diagnostics.config_fetch_duration_ms;
  }

  const onLine = diagnostics.navigator_on_line ?? resolveNavigatorOnLineTag();
  if (onLine !== undefined) merged.navigator_on_line = onLine;

  return merged;
}

function applyDiagnosticsTags(
  scope: Sentry.Scope,
  input: CaptureScopeInput,
  mergedExtra: Record<string, string | number | undefined>,
): void {
  const diagnostics = input.diagnostics ?? {};

  if (mergedExtra.network_reason) {
    scope.setTag("network_reason", String(mergedExtra.network_reason));
  }
  if (diagnostics.realtime_failure_phase ?? mergedExtra.realtime_failure_phase) {
    scope.setTag(
      "realtime_failure_phase",
      String(diagnostics.realtime_failure_phase ?? mergedExtra.realtime_failure_phase),
    );
  }
  if (diagnostics.realtime_connect_trigger ?? mergedExtra.realtime_connect_trigger) {
    scope.setTag(
      "realtime_connect_trigger",
      String(diagnostics.realtime_connect_trigger ?? mergedExtra.realtime_connect_trigger),
    );
  }
  if (diagnostics.route_feature ?? mergedExtra.route_feature) {
    scope.setTag("route_feature", String(diagnostics.route_feature ?? mergedExtra.route_feature));
  }
  const axiosCode =
    diagnostics.axios_code ?? mergedExtra.axios_code ?? resolveAxiosCodeTag(input.axiosError);
  if (axiosCode) {
    mergedExtra.axios_code = axiosCode;
    scope.setTag("axios_code", String(axiosCode));
  }
  if (mergedExtra.document_hidden !== undefined) {
    scope.setTag("document_hidden", String(mergedExtra.document_hidden));
  }
  if (mergedExtra.connection_effective_type) {
    scope.setTag("connection_effective_type", String(mergedExtra.connection_effective_type));
  }
  if (mergedExtra.sw_active !== undefined) {
    scope.setTag("sw_active", String(mergedExtra.sw_active));
  }

  const contextEntries = Object.fromEntries(
    Object.entries(mergedExtra).filter(([, value]) => value !== undefined),
  );
  if (Object.keys(contextEntries).length > 0) {
    scope.setContext("diagnostics", contextEntries);
  }
}

function applyCaptureScope(scope: Sentry.Scope, input: CaptureScopeInput): void {
  scope.setTag("feature", input.feature);
  scope.setTag("surface", "web");
  scope.setTag("error_source", input.errorSource);

  if (input.errorSource === "telemetry") {
    if (input.eventType) scope.setTag("event_type", input.eventType);
    if (input.sheetId) scope.setTag("sheet_id", input.sheetId);
    scope.setFingerprint([
      "telemetry",
      input.eventType ?? "unknown",
      input.feature,
      input.operation ?? "none",
    ]);
  } else if (input.errorCode) {
    scope.setTag("error_code", input.errorCode);
    scope.setFingerprint([input.feature, input.errorCode]);
  }

  if (input.failureDomain) scope.setTag("failure_domain", input.failureDomain);
  if (input.operation) scope.setTag("operation", input.operation);

  if (input.http?.httpMethod) scope.setTag("http_method", input.http.httpMethod);
  if (input.http?.httpPath) scope.setTag("http_path", input.http.httpPath);
  if (input.http?.httpStatus) scope.setTag("http_status", input.http.httpStatus);

  const route = getRouteTag();
  if (route) {
    scope.setTag("route", route);
    scope.setTransactionName(route);
  }

  const mergedExtra = mergeDiagnosticsExtra(input);
  applyDiagnosticsTags(scope, input, mergedExtra);

  for (const [key, value] of Object.entries(mergedExtra)) {
    if (value !== undefined) scope.setExtra(key, value);
  }
}

export type CaptureGlitchtipEventInput = {
  error?: unknown;
  feature?: string;
  errorCode?: string;
  eventType?: string;
  sheetId?: string;
  failureDomain?: FailureDomain;
  operation?: string;
  errorSource: ErrorSource;
  axiosError?: AxiosError;
  http?: HttpContext;
  level?: Sentry.SeverityLevel;
  extra?: Record<string, string | number | undefined>;
  diagnostics?: GlitchtipDiagnostics;
  /** When true, capture human title via captureMessage (axios 5xx / network). */
  useMessageTitle?: boolean;
  /** When true, bypass the 3s duplicate capture guard (camera diagnostics). */
  skipDedupe?: boolean;
};

/** Central GlitchTip capture — axios, ErrorBoundary, and domain paths must use this. */
export function captureGlitchtipEvent(input: CaptureGlitchtipEventInput): void {
  if (!GLITCHTIP_DSN) return;
  if (!shouldCaptureToGlitchtip(input)) return;

  const axiosError =
    input.axiosError ??
    (input.error && typeof input.error === "object" && "isAxiosError" in input.error
      ? (input.error as AxiosError)
      : undefined);

  if (input.errorSource === "axios" && axiosError && !shouldCaptureAxiosError(axiosError)) {
    return;
  }

  const feature =
    input.feature ??
    resolveFeature({
      metaFeature: axiosError?.config?.meta?.feature,
      pathname: resolveFeatureFromWindowLocation(),
      apiUrl: axiosError?.config?.url,
    });

  if (input.errorSource === "telemetry") {
    if (!input.eventType) return;

    const dedupeKey = ["telemetry", feature, input.eventType, input.operation ?? "", input.sheetId ?? ""].join(
      "|",
    );
    if (!input.skipDedupe && shouldSkipDuplicateCapture(dedupeKey)) return;

    Sentry.withScope((scope) => {
      applyCaptureScope(scope, {
        feature,
        errorSource: "telemetry",
        eventType: input.eventType,
        sheetId: input.sheetId,
        failureDomain: input.failureDomain,
        operation: input.operation,
        extra: input.extra,
        diagnostics: input.diagnostics,
      });
      const level = input.level ?? "warning";
      scope.setLevel(level);
      Sentry.captureMessage(getTelemetryTitle(feature, input.eventType!), {level});
    });
    return;
  }

  const deviceOverride = axiosError ? resolveAxiosDeviceFailureOverride(axiosError) : null;
  const errorCode =
    input.errorCode ??
    deviceOverride?.errorCode ??
    (input.error ? resolveErrorCode(input.error) : undefined);
  if (!errorCode) return;

  const http = input.http ?? (axiosError ? extractHttpContextFromAxios(axiosError) : undefined);
  const operation = input.operation ?? axiosError?.config?.meta?.operation;
  const level =
    input.level ??
    deviceOverride?.level ??
    (axiosError ? resolveAxiosCaptureLevel(axiosError) : "error");
  const failureDomain = resolveFailureDomain(errorCode, input.failureDomain ?? deviceOverride?.failureDomain);

  const dedupeKey = [
    feature,
    errorCode,
    operation ?? "",
    http?.httpPath ?? "",
    http?.httpStatus ?? "",
    input.errorSource,
  ].join("|");
  if (!input.skipDedupe && shouldSkipDuplicateCapture(dedupeKey)) return;

  Sentry.withScope((scope) => {
    applyCaptureScope(scope, {
      feature,
      errorCode,
      failureDomain,
      errorSource: input.errorSource,
      operation,
      http,
      extra: input.extra,
      diagnostics: input.diagnostics,
      error: input.error,
      axiosError,
    });
    scope.setLevel(level);

    const title = getEventTitle(feature, errorCode);

    if (input.useMessageTitle || input.error === undefined) {
      Sentry.captureMessage(title, {level});
      return;
    }

    Sentry.captureException(input.error);
  });
}

type DomainCaptureInput = {
  feature: string;
  errorCode: string;
  failureDomain?: FailureDomain;
  operation?: string;
  level?: Sentry.SeverityLevel;
  message: string;
  axiosError?: AxiosError;
  http?: HttpContext;
  extra?: Record<string, string | number | undefined>;
  diagnostics?: GlitchtipDiagnostics;
  error?: unknown;
};

export function captureDomainEvent(input: DomainCaptureInput): void {
  captureGlitchtipEvent({
    feature: input.feature,
    errorCode: input.errorCode,
    failureDomain: input.failureDomain,
    operation: input.operation,
    errorSource: "domain",
    axiosError: input.axiosError,
    http: input.http,
    level: input.level ?? "warning",
    extra: input.extra,
    diagnostics: input.diagnostics,
    error: input.error,
    useMessageTitle: true,
  });
}

export function captureTaggedException(
  error: unknown,
  input: {
    feature?: string;
    errorCode?: string;
    operation?: string;
    errorSource?: ErrorSource;
    axiosError?: AxiosError;
    extra?: DomainCaptureInput["extra"];
    diagnostics?: GlitchtipDiagnostics;
    useMessageTitle?: boolean;
  },
): void {
  captureGlitchtipEvent({
    error,
    feature: input.feature,
    errorCode: input.errorCode,
    operation: input.operation,
    errorSource: input.errorSource ?? "domain",
    axiosError: input.axiosError,
    extra: input.extra,
    diagnostics: input.diagnostics,
    useMessageTitle: input.useMessageTitle,
  });
}

export function captureAxiosError(error: AxiosError): void {
  if (!GLITCHTIP_DSN) return;

  const meta = error.config?.meta;
  const feature = resolveFeature({
    metaFeature: meta?.feature,
    pathname: resolveFeatureFromWindowLocation(),
    apiUrl: error.config?.url,
  });
  const deviceOverride = resolveAxiosDeviceFailureOverride(error);

  if (!shouldCaptureAxiosError(error)) {
    if (deviceOverride) {
      const path = normalizeHttpPathTag(error.config?.url);
      if (
        deviceOverride.errorCode === DEVICE_FAILURE_ERROR_CODES.REALTIME_CONNECTION_FAILED &&
        path === "broadcasting/config"
      ) {
        // socket.tsx owns config_fetch with duration + attempt count
        return;
      }

      const http = extractHttpContextFromAxios(error);

      const routeFeature = resolveFeatureFromWindowLocation();
      const realtimePhase =
        deviceOverride.errorCode === DEVICE_FAILURE_ERROR_CODES.BROADCASTING_AUTH_FAILED
          ? "ws_auth"
          : "config_fetch";

      if (deviceOverride.errorCode === DEVICE_FAILURE_ERROR_CODES.REALTIME_CONNECTION_FAILED) {
        if (!sessionCaptureOnce(`realtime:${realtimePhase}`)) return;
      } else if (deviceOverride.errorCode === DEVICE_FAILURE_ERROR_CODES.BROADCASTING_AUTH_FAILED) {
        if (!sessionCaptureOnce("realtime:ws_auth")) return;
      } else if (deviceOverride.errorCode === DEVICE_FAILURE_ERROR_CODES.PUSH_SUBSCRIPTION_FAILED) {
        if (!sessionCaptureOnce("notification:subscription_failed")) return;
      }

      const captureFeature =
        deviceOverride.failureDomain === "realtime" ? PLATFORM_FEATURE : feature;

      captureGlitchtipEvent({
        feature: captureFeature,
        errorCode: deviceOverride.errorCode,
        failureDomain: deviceOverride.failureDomain,
        operation: meta?.operation,
        errorSource: "domain",
        axiosError: error,
        http,
        level: deviceOverride.level ?? "warning",
        diagnostics: {
          route_feature: routeFeature,
          realtime_failure_phase:
            deviceOverride.failureDomain === "realtime" ? realtimePhase : undefined,
          error_kind: resolveErrorKind(error),
          axios_code: resolveAxiosCodeTag(error),
          ...(deviceOverride.failureDomain === "realtime"
            ? collectRealtimeClientDiagnostics()
            : {}),
        },
        error,
        useMessageTitle: true,
      });
    }
    return;
  }

  const errorCode = resolveErrorCode(error);

  captureGlitchtipEvent({
    error,
    feature,
    errorCode,
    operation: meta?.operation,
    errorSource: "axios",
    axiosError: error,
    useMessageTitle: true,
  });
}

export function applyRenderErrorScope(scope: Sentry.Scope, pathname?: string): void {
  const feature = pathname
    ? resolveFeature({pathname})
    : resolveFeatureFromWindowLocation();

  applyCaptureScope(scope, {
    feature,
    errorCode: SYNTHETIC_ERROR_CODES.REACT_RENDER_ERROR,
    failureDomain: "crash",
    errorSource: "boundary",
  });
}

export function captureRenderError(error: unknown, pathname?: string): void {
  captureGlitchtipEvent({
    error,
    feature: pathname ? resolveFeature({pathname}) : resolveFeatureFromWindowLocation(),
    errorCode: SYNTHETIC_ERROR_CODES.REACT_RENDER_ERROR,
    failureDomain: "crash",
    errorSource: "boundary",
    level: "error",
    useMessageTitle: true,
  });
}
